/* eslint-disable no-duplicate-imports */
/* eslint-disable prettier/prettier */
/* eslint-disable react/jsx-key */

import {
  Box,
  Grid,
  Dialog,
  Typography,
  DialogContent,
  Divider,
  Button,
} from '@mui/material';

import {
  topicArray,
  bioFormatter,
  industryArray,
  capitalizeFirstLetter,
  formatNumberWithCommas,
} from '../../../components/common';

import MyChip from '../../../components/MyChip';
import ChatAvatarComponent from '../../chat/ChatAvatarComponent';
import UserinfoCard from '../../../components/UserinfoCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MatchDetailsPopupLoader from './MatchDetailsPopupLoader';
import CommonModalHeader from '../../../../../components/DialogUi/CommonModalHeader';
import { useMemberWord } from '../../../../../utils/memberTypeLabels';
import { SvgIcon } from "@mui/material";

const DoubleArrowIcon = ({ color = "#868686", size = 24, ...props }) => {
  return (
    <SvgIcon
      {...props}
      viewBox="0 0 24 24"
      sx={{
        width: size,
        height: size,
      }}
    >
      {/* Left arrow */}
      <path
        d="M10 6 L4 12 L10 18"
        stroke={color}
        strokeWidth="2"
        fill="none"
        strokeLinecap="square"
      />

      {/* Right arrow */}
      <path
        d="M14 6 L20 12 L14 18"
        stroke={color}
        strokeWidth="2"
        fill="none"
        strokeLinecap="square"
      />
    </SvgIcon>
  );
};



const MatchUserCard = ({ name, headshot, role }: any) => {
  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        alignItems: 'flex-start',
        '*': {
        fontFamily: 'Inter !important',
      },
      '*::before': {
        fontFamily: 'Inter !important',
      },
      '*::after': {
        fontFamily: 'Inter !important',
      },
      }}
    >
      {/* Avatar + Name */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '5px',
        }}
      >
        {/* Avatar */}
        <ChatAvatarComponent
          image={headshot}
          firstLetter={name?.charAt(0) || ''}
          width="75px"
          height="75px"
          type="noStatus"
        />

        {/* Name */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '5px',
            alignItems: 'center',
            width: '100%',
            '*': {
        fontFamily: 'Inter !important',
      },
      '*::before': {
        fontFamily: 'Inter !important',
      },
      '*::after': {
        fontFamily: 'Inter !important',
      },
          }}
        >
          <Typography
            sx={{
              color: 'var(--Text-text-primary, #FDFDFD)',
              fontSize: 14,
              fontFamily: 'Inter',
              fontWeight: 700,
              lineHeight: '14px',
              textAlign: 'center',
            }}
          >
            {name || ''}
          </Typography>
        </Box>
      </Box>

      {/* Role */}
      <Typography
        sx={{
          alignSelf: 'stretch',
          textAlign: 'center',
          color: 'var(--Stroke-highlight, #F79420)',
          fontSize: 12,
          fontFamily: 'Inter',
          fontWeight: 500,
          lineHeight: '12px',
          '*': {
        fontFamily: 'Inter !important',
      },
      '*::before': {
        fontFamily: 'Inter !important',
      },
      '*::after': {
        fontFamily: 'Inter !important',
      },
        }}
      >
        {role}
      </Typography>
    </Box>
  );
};

interface MatchDetailsProps {
  openMatchDetails: any;
  handleCloseMatchDetails: any;
  matchDetails: any;
  matchDetailsLoader: any;
  matchId?: string;
  /** The match's status as its row shows it (Pending / Ongoing / Ended): getMatchProfile sends none. */
  statusLabel?: string;
}

function MatchDetailsPopup({
  openMatchDetails,
  handleCloseMatchDetails,
  matchDetails,
  matchDetailsLoader,
  matchId,
  statusLabel,
}: MatchDetailsProps) {
  const w = useMemberWord();
  const loading = matchDetailsLoader && !matchDetails;

  // Learning objectives shared by this mentee and this mentor: filter mentee.learningObjectives where mentors includes current mentor
  const objectivesWithThisMentor = (() => {
    const list = matchDetails?.mentee?.learningObjectives;
    const mentor = matchDetails?.mentor;
    if (!Array.isArray(list) || !list.length || !mentor) return [];
    const mentorId = mentor.userId || mentor.mid || mentor.id;
    if (!mentorId) return [];
    return list.filter((obj: any) =>
      (obj?.mentors || []).some(
        (m: any) => (m?.userId || m?.mid || m?.id) === mentorId
      )
    );
  })();
  const topics = topicArray();
  const industries = industryArray();

  const timestamp = matchDetails?.startDate;
  const date = timestamp ? new Date(timestamp * 1000) : null;
  const formattedDate = date
    ? date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : null;

  /** BIO */
  const getBio = (mem: any) => {
    const { education, workHistory } = mem?.bio || {};
    const bio1 = bioFormatter(education?.major, education?.university);
    const bio2 = bioFormatter(workHistory?.role, workHistory?.companyName);
    return bio1 || bio2 || '';
  };

  /** USER CARDS */
  const UserinfoCardDetails = {
    mentee: {
      headshot: matchDetails?.mentee?.headshot,
      displayName: matchDetails?.mentee?.displayName,
      UserType: 'Mentee',
      bio: getBio(matchDetails?.mentee),
    },
    mentor: {
      headshot: matchDetails?.mentor?.headshot,
      displayName: matchDetails?.mentor?.displayName,
      UserType: 'Mentor',
      bio: getBio(matchDetails?.mentor),
    },
  };

  /** HEADER MIDDLE (MATCH TYPE + DATE) */
  const middleBox = () => (
    <Box
      sx={{
        width: 160,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        alignItems: 'center',
        '*': {
        fontFamily: 'Inter !important',
      },
      '*::before': {
        fontFamily: 'Inter !important',
      },
      '*::after': {
        fontFamily: 'Inter !important',
      },
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          color: 'var(--Text-text-primary, #FDFDFD)',
          fontFamily: 'Inter',
          fontWeight: 500,
          textAlign: 'center',
        }}
      >
        Objective Match
      </Typography>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <DoubleArrowIcon />
      </Box>

      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 500,
          '*': {
        fontFamily: 'Inter !important',
      },
      '*::before': {
        fontFamily: 'Inter !important',
      },
      '*::after': {
        fontFamily: 'Inter !important',
      },
          color: 'var(--Text-text-primary, #FDFDFD)',
          textAlign: 'center',
        }}
      >
        {formattedDate || matchDetails?.startDate}
      </Typography>

      <Typography
        sx={{
          fontSize: 12,
          color: 'var(--Stroke-highlight, #F79420)',
          fontFamily: 'Inter',
          fontWeight: 500,
          textAlign: 'center',
        }}
      >
        {statusLabel || matchDetails?.status || 'Ongoing'}
      </Typography>
    </Box>
  );

  /** RENDER CHIPS */
  const renderChips = (values: any[], bg?: string) => (
    <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
      {values.map((v, idx) => (
        <Typography color='white' key={idx} >{v}</Typography>
      ))}
    </Box>
  );

  /** MAIN CONTENT */
  const getMatchDetails = () => (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* TOP SECTION (MENTEE — MIDDLE — MENTOR) */}
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <MatchUserCard
  name={UserinfoCardDetails.mentee.displayName}
  headshot={UserinfoCardDetails.mentee.headshot}
  role={w('mentee')}
/>

        {middleBox()}

       <MatchUserCard
  name={UserinfoCardDetails.mentor.displayName}
  headshot={UserinfoCardDetails.mentor.headshot}
  role={w('mentor')}
/>
      </Box>

      <Divider sx={{ background: 'var(--Stroke-divider, #444444)', height: '1px !important' }} />

      {/* METRICS (Past / Future / Messages) */}
      <Box sx={{ display: 'flex', width: '100%', gap: 4,'*': {
        fontFamily: 'Inter !important',
      },
      '*::before': {
        fontFamily: 'Inter !important',
      },
      '*::after': {
        fontFamily: 'Inter !important',
      }, }}>
        {[
          { label: 'Past calls', value: matchDetails?.pastCall },
          { label: 'Future calls', value: matchDetails?.futureCall },
          { label: 'Messages shared', value: matchDetails?.messagedShared },
        ].map((item, idx) => (
          <Box key={idx} sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              sx={{
                fontSize: 12,
                color: 'var(--Text-text-tertiary, #868686)',
                fontWeight: 700,
                fontFamily: 'Inter',
              }}
            >
              {item.label}
            </Typography>

            <Typography
              sx={{
                fontSize: 14,
                color: 'var(--Text-text-primary, #FDFDFD)',
                fontWeight: 400,
                fontFamily: 'Inter',
              }}
            >
              {formatNumberWithCommas(item.value) || '---'}
            </Typography>
          </Box>
        ))}
      </Box>

      <Divider sx={{ background: 'var(--Stroke-Border-border, #444444)' }} />

      {/* LEARNING OBJECTIVES (mentee LOs that have this mentor) */}
      <Typography
        sx={{
          fontSize: 16,
          fontFamily: 'Inter',
          fontWeight: 700,
          color: 'var(--Text-text-primary, #FDFDFD)',
        }}
      >
        Learning objectives
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {objectivesWithThisMentor.length === 0 ? (
          <Typography
            sx={{
              fontSize: 14,
              color: 'var(--Text-text-tertiary, #868686)',
              fontFamily: 'Inter',
              fontWeight: 400,
            }}
          >
            None
          </Typography>
        ) : (
          objectivesWithThisMentor.map((obj: any, idx: number) => {
            const status = obj?.status ?? 'Not set';
            const normalizedStatus = String(status).toLowerCase().replace(/[\s_-]/g, '');
            const isCompleted = ['completed', 'complete', 'done'].includes(
              normalizedStatus
            );
            const isTodo = ['todo', 'notstarted'].includes(normalizedStatus);
            const statusLabel = isTodo ? 'Not completed' : status;
            return (
              <Box
                key={obj?.id ?? obj?.objective ?? idx}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  py: 0.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 14,
                    color: 'var(--Text-text-primary, #FDFDFD)',
                    fontFamily: 'Inter',
                    fontWeight: 400,
                  }}
                >
                  {obj?.objective ?? obj?.name ?? 'Not set'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: 'var(--Text-text-tertiary, #868686)',
                      fontFamily: 'Inter',
                      fontWeight: 600,
                    }}
                  >
                    Status:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: isCompleted
                        ? 'var(--UI-Success, #00972D)'
                        : 'var(--Text-text-primary, #FDFDFD)',
                      fontFamily: 'Inter',
                      fontWeight: 600,
                      textTransform: isTodo ? 'none' : 'capitalize',
                    }}
                  >
                    {statusLabel}
                  </Typography>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {/* MEETING HISTORY (past, upcoming, canceled from matchDetails.meetingHistory) */}
      <Typography
        sx={{
          fontSize: 16,
          fontFamily: 'Inter',
          fontWeight: 700,
          color: 'var(--Text-text-primary, #FDFDFD)',
        }}
      >
        Meeting history
      </Typography>

      {(() => {
        const meetingHistory = matchDetails?.meetingHistory || {};
        const past = Array.isArray(meetingHistory.past) ? meetingHistory.past : [];
        const upcoming = Array.isArray(meetingHistory.upcoming) ? meetingHistory.upcoming : [];
        const canceled = Array.isArray(meetingHistory.canceled) ? meetingHistory.canceled : [];
        const incomplete = Array.isArray(meetingHistory.incomplete) ? meetingHistory.incomplete : [];
        const hasAny = past.length > 0 || upcoming.length > 0 || canceled.length > 0 || incomplete.length > 0;

        const formatMeetingDate = (m: any) => {
          const d = m?.callDate;
          const t = m?.callTime;
          if (d && t) return `${d} ${t}`;
          if (d) return String(d);
          return 'Not set';
        };

        const getMeetingStatus = (m: any) => {
          const status = String(m?.callStatus || '').toLowerCase();
          if (status.includes('cancel')) return { label: 'Canceled', color: '#868686' };
          if (status.includes('incomplete') || status.includes('no-show') || status.includes('missed') || status.includes('no show')) return { label: 'Incomplete', color: '#F79420' };
          return { label: 'Completed', color: '#00972D' };
        };

        if (!hasAny) {
          return (
            <Typography
              sx={{
                fontSize: 14,
                color: 'var(--Text-text-tertiary, #868686)',
                fontFamily: 'Inter',
                fontWeight: 400,
              }}
            >
              No meetings yet.
            </Typography>
          );
        }

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {past.length > 0 && (
              <Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    color: 'var(--Text-text-tertiary, #868686)',
                    mb: 1,
                  }}
                >
                  Past meetings ({past.length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {past.map((m: any, idx: number) => {
                    const statusInfo = getMeetingStatus(m);
                    const isCanceled = statusInfo.label === 'Canceled';
                    const menteeReviewData = m?.menteeReview ?? m?.menteeRating;
                    const mentorReviewData = m?.mentorReview ?? m?.mentorRating;
                    const menteeReviewText =
                      menteeReviewData?.review ?? (typeof menteeReviewData === 'string' ? menteeReviewData : null) ?? '';
                    const menteeReviewRating = Number(menteeReviewData?.rating ?? menteeReviewData?.vote ?? 0);
                    const mentorReviewText =
                      mentorReviewData?.review ?? (typeof mentorReviewData === 'string' ? mentorReviewData : null) ?? mentorReviewData?.feedback ?? '';
                    const mentorReviewRating = Number(mentorReviewData?.rating ?? mentorReviewData?.vote ?? 0);
                    const mentorName = matchDetails?.mentor?.displayName ?? matchDetails?.mentor?.name ?? w('mentor');
                    const mentorHeadshot = matchDetails?.mentor?.headshot ?? '';
                    const menteeName = matchDetails?.mentee?.displayName ?? matchDetails?.mentee?.name ?? w('mentee');
                    const feedbackDate = mentorReviewData?.date ?? mentorReviewData?.createdAt ?? m?.callDate;
                    const dateFormatted = feedbackDate
                      ? (typeof feedbackDate === 'string' ? new Date(feedbackDate) : new Date(feedbackDate * 1000)).toLocaleDateString('en-US', {
                          month: 'numeric',
                          day: 'numeric',
                          year: '2-digit',
                        })
                      : formatMeetingDate(m);
                    return (
                      <Box
                        key={m?.meetingId ?? idx}
                        sx={{
                          p: 1.75,
                          borderRadius: '5px',
                          border: '1px solid #444444',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1.25,
                          bgcolor: 'rgba(255,255,255,0.04)',
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700, fontFamily: 'Inter' }}>
                              {m?.callTitle ?? `Meeting ${idx + 1}`}
                            </Typography>
                            <Box
                              sx={{
                                px: 1.5,
                                py: 0.375,
                                borderRadius: '20px',
                                bgcolor: statusInfo.color,
                                color: '#fff',
                                fontSize: 12,
                                fontWeight: 400,
                                fontFamily: 'Inter',
                              }}
                            >
                              {statusInfo.label}
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                              <AccessTimeIcon sx={{ fontSize: 16, color: '#B6B6B6' }} />
                              <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontFamily: 'Inter' }}>
                                {formatMeetingDate(m)}
                              </Typography>
                            </Box>
                            {m?.callStatus && !isCanceled && (
                              <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontFamily: 'Inter' }}>
                                {m.callStatus}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        {!isCanceled && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontFamily: 'Inter' }}>
                                {w('mentee')} Review:
                              </Typography>
                              {menteeReviewText ? (
                                <Typography sx={{ color: '#B6B6B6', fontSize: 14, fontWeight: 500, fontFamily: 'Inter' }}>
                                  {menteeReviewText}
                                </Typography>
                              ) : (
                                <Typography sx={{ color: '#868686', fontSize: 14, fontStyle: 'italic', fontFamily: 'Inter' }}>
                                  No review yet
                                </Typography>
                              )}
                              {menteeReviewRating > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontFamily: 'Inter' }}>Rated:</Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                    {[1, 2, 3, 4, 5].map((star) => (
                                      <StarIcon
                                        key={star}
                                        sx={{
                                          fontSize: 16,
                                          color: star <= menteeReviewRating ? '#FDFDFD' : '#444444',
                                        }}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                            <Box
                              sx={{
                                p: 1.25,
                                bgcolor: '#1C1C1C',
                                borderRadius: '6px',
                                border: '1px solid #2A2A2A',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                              }}
                            >
                              <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontFamily: 'Inter' }}>
                                {w('mentor')} Review:
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                                <ChatAvatarComponent
                                  image={mentorHeadshot}
                                  firstLetter={mentorName?.charAt(0) || ''}
                                  width="40px"
                                  height="40px"
                                  type="noStatus"
                                />
                                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700, fontFamily: 'Inter' }}>
                                      {mentorName}
                                    </Typography>
                                    <Typography sx={{ color: '#B6B6B6', fontSize: 10, fontWeight: 500, fontFamily: 'Inter' }}>
                                      {dateFormatted}
                                    </Typography>
                                  </Box>
                                  {mentorReviewText ? (
                                    <Typography sx={{ color: '#B6B6B6', fontSize: 14, fontWeight: 400, fontFamily: 'Inter' }}>
                                      {mentorReviewText}
                                    </Typography>
                                  ) : (
                                    <Typography sx={{ color: '#868686', fontSize: 14, fontStyle: 'italic', fontFamily: 'Inter' }}>
                                      No review yet
                                    </Typography>
                                  )}
                                  {mentorReviewRating > 0 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                      {[1, 2, 3, 4, 5].map((star) => (
                                        <StarIcon
                                          key={star}
                                          sx={{
                                            fontSize: 14,
                                            color: star <= mentorReviewRating ? '#FDFDFD' : '#444444',
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {incomplete.length > 0 && (
              <Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    color: 'var(--Stroke-highlight, #F79420)',
                    mb: 1,
                  }}
                >
                  Incomplete meetings ({incomplete.length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {incomplete.map((m: any, idx: number) => (
                    <Box
                      key={m?.meetingId ?? `incomplete-${idx}`}
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: 'rgba(247,148,32,0.08)',
                        border: '1px solid rgba(247,148,32,0.35)',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#FDFDFD', fontFamily: 'Inter' }}>
                          {m?.callTitle ?? `Meeting ${idx + 1}`}
                        </Typography>
                        <Box
                          sx={{
                            px: 1.5,
                            py: 0.375,
                            borderRadius: '20px',
                            bgcolor: '#F79420',
                            color: '#fff',
                            fontSize: 12,
                            fontWeight: 400,
                            fontFamily: 'Inter',
                          }}
                        >
                          Incomplete
                        </Box>
                      </Box>
                      <Typography sx={{ fontSize: 12, color: '#B6B6B6', fontFamily: 'Inter', mt: 0.5 }}>
                        {formatMeetingDate(m)}
                      </Typography>
                      {m?.callStatus && (
                        <Typography sx={{ fontSize: 12, color: '#868686', fontFamily: 'Inter', mt: 0.25 }}>
                          {m.callStatus}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {upcoming.length > 0 && (
              <Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    color: 'var(--Stroke-highlight, #F79420)',
                    mb: 1,
                  }}
                >
                  Upcoming meetings ({upcoming.length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {upcoming.map((m: any, idx: number) => (
                    <Box
                      key={m?.meetingId ?? idx}
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: 'rgba(247,148,32,0.08)',
                        border: '1px solid rgba(247,148,32,0.3)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      }}
                    >
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#FDFDFD', fontFamily: 'Inter' }}>
                        {m?.callTitle ?? `Upcoming meeting ${idx + 1}`}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: '#B6B6B6', fontFamily: 'Inter' }}>
                        {formatMeetingDate(m)}
                      </Typography>
                      {m?.meetingLink && (
                        <Button
                          component="a"
                          href={m.meetingLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                          sx={{
                            alignSelf: 'flex-start',
                            mt: 0.5,
                            fontSize: 12,
                            fontWeight: 600,
                            textTransform: 'none',
                            background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
                            color: '#fff',
                            '&:hover': { background: 'linear-gradient(180deg, #E0851a 0%, #d94e20 100%)' },
                          }}
                        >
                          Join
                        </Button>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {canceled.length > 0 && (
              <Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    color: 'var(--Text-text-tertiary, #868686)',
                    mb: 1,
                  }}
                >
                  Canceled ({canceled.length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {canceled.map((m: any, idx: number) => (
                    <Box
                      key={m?.meetingId ?? idx}
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: 'rgba(255,255,255,0.02)',
                        border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
                        opacity: 0.85,
                      }}
                    >
                      <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#868686', fontFamily: 'Inter' }}>
                        {m?.callTitle ?? `Canceled meeting ${idx + 1}`}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: '#666', fontFamily: 'Inter', mt: 0.5 }}>
                        {formatMeetingDate(m)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        );
      })()}
    </Box>
  );

  return (
  <Box
    sx={{
      '*': {
        fontFamily: 'Inter !important',
      },
      '*::before': {
        fontFamily: 'Inter !important',
      },
      '*::after': {
        fontFamily: 'Inter !important',
      },
    }}
  >
    <Dialog
      fullWidth
      open={openMatchDetails}
      onClose={handleCloseMatchDetails}
      PaperProps={{
        sx: {
          background: 'var(--Fill-Modal-background, #0F0F0F)',
          color: '#FDFDFD',
          borderRadius: '8px',
          outline: '1px solid var(--Stroke-Modal-border, #444444)',
          outlineOffset: '-1px',
        },
      }}
    >
      <CommonModalHeader
        title="Match details"
        handleCloseModal={handleCloseMatchDetails}
      />

      <DialogContent sx={{ p: 3 }}>
        {loading ? <MatchDetailsPopupLoader /> : getMatchDetails()}
      </DialogContent>
    </Dialog>
  </Box>
);

}

export default MatchDetailsPopup;
